import { Component, Input, OnInit, forwardRef, Inject } from '@angular/core';
import { TranslateService }        from '@ngx-translate/core';

import { ApiService }         from '../api/api.service';
import { BigDecimalService }  from '../core/services/big-decimal.service';
import { CompanyService }     from '../companies/company.service';
import { ConfirmService }     from '../messages/confirm/confirm.service';
import { MessagesService }    from '../messages/messages.service';
import { XeroService }        from '../xero/xero.service';
import { CrudGridService, CrudGridInstance } from '../grids/crud-grid.service';

import { ProductCategoryModel } from './models/product-category';

@Component({
  selector: 'wor-company-lists-product-categories',
  template: require('./company-lists.product-categories.component.html')
})
export class CompanyListsProductCategoriesComponent implements OnInit {
  @Input() productCategories: Array<ProductCategoryModel> = [];

  crudGrid: CrudGridInstance<ProductCategoryModel>;
  collapsed = false;
  previousValue: ProductCategoryModel = {} as ProductCategoryModel;
  taxRates: Array<any> = [];

  constructor (
    @Inject(forwardRef(() => ApiService)) private apiService: ApiService,
    @Inject(forwardRef(() => BigDecimalService))
    private bigDecimalService: BigDecimalService,
    @Inject(forwardRef(() => CompanyService))
    public companyService: CompanyService,
    @Inject(forwardRef(() => ConfirmService)) private confirmService: ConfirmService,
    @Inject(forwardRef(() => CrudGridService)) private crudGridService: CrudGridService,
    @Inject(forwardRef(() => MessagesService)) private messagesService: MessagesService,
    private translateService: TranslateService,
    @Inject(forwardRef(() => XeroService)) private xeroService: XeroService
  ) {}

  ngOnInit(): void {
    this.crudGrid = this.crudGridService.create<ProductCategoryModel>();
    this.crudGrid.data = this.productCategories;

    if (this.companyService.hasIntegration('quickbooks')) {
      this.apiService.get('/quickbooks_online/tax_rates')
        .then((rates) => {
          this.taxRates = rates.all_tax_rates;
        })
        .catch(err => {
          this.messagesService.error(err);
        });
    } else if (this.companyService.hasIntegration('xero')) {
      this.xeroService.getTaxRates()
        .then((rates: Array<any>) => {
          this.taxRates = rates;
        })
        .catch(err => {
          this.messagesService.error(err);
        });
    }
  }

  cancelEdit(category: ProductCategoryModel, index: number): void {
    const restored = { ...this.previousValue, editing: true };
    this.crudGrid.data[index] = restored;
    this.crudGrid.toggleEditMode(restored, index);
  }

  editRow(category: ProductCategoryModel, index: number): void {
    this.previousValue = { ...category };
    this.crudGrid.editRow(category, index);
  }

  getTaxRate(taxRate: any): any {
    return (taxRate || taxRate === 0)
      ? this.bigDecimalService.get(taxRate) as any
      : null;
  }

  isQuickbooksTaxTypeAvailable(): boolean {
    return this.companyService.hasIntegration('quickbooks')
      && !this.companyService.hasIntegration('xero')
      && !this.companyService.hasIntegration('myob');
  }

  isTaxRateAvailable(): boolean {
    return !this.companyService.usesMultiTax()
      && !this.companyService.hasIntegration('myob');
  }

  isTaxRateEditable(): boolean {
    return !this.isQuickbooksTaxTypeAvailable() && !this.isXeroTaxTypeAvailable();
  }

  isXeroTaxTypeAvailable(): boolean {
    return this.companyService.hasIntegration('xero')
      && !this.companyService.hasIntegration('quickbooks')
      && !this.companyService.hasIntegration('myob');
  }

  removeRow(category: ProductCategoryModel, index: number): void {
    this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_TEMPLATE'))
      .then(() => this.crudGrid.removeRow(category, index, '/product_category', 'id'))
      .then(() => {
        this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.DELETED.PRODUCT_CATEGORY'), 'success');
        this.crudGrid.data.splice(index, 1);
      });
  }

  saveCategory(category: ProductCategoryModel, index: number): void {
    const method = category.id ? 'patch' : 'post';
    const url    = category.id ? '/product_category' : '/product_categories';

    this.apiService[method](url, category, 'product_category')
      .then((result: Array<ProductCategoryModel> | ProductCategoryModel) => {
        if (Array.isArray(result)) {
          Object.assign(category, result.find(pc => pc.id === category.id));
        } else {
          this.crudGrid.data.push(result);
        }

        this.crudGrid.toggleEditMode(category, index);
        this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.PRODUCT_CATEGORY'), 'success');
      })
      .catch(err => {
        this.messagesService.error(err);
      });
  }

  saveRow(category: ProductCategoryModel, index: number): void {
    if (this.companyService.hasIntegration('myob')) {
      category.purchase_tax_rate = null;
      category.sales_tax_rate    = null;
    }

    const purchasesTaxRate = this.getTaxRate(category.purchase_tax_rate);
    const salesTaxRate     = this.getTaxRate(category.sales_tax_rate);

    Promise.resolve()
      .then(() =>
        (this.isTaxRateAvailable()
          && ((salesTaxRate && salesTaxRate.isZero()) || (purchasesTaxRate && purchasesTaxRate.isZero()))
        )
        ? this.confirmService.generic(this.translateService.instant('COMPANY_SPACE.LISTS.TAX_RATE_IS_ZERO'))
        : Promise.resolve(true)
      )
      .then(() => {
        this.saveCategory(category, index);
      })
      .catch(() => {
        category.sales_tax_rate = salesTaxRate?.isZero()
          ? this.previousValue.sales_tax_rate
          : category.sales_tax_rate;

        category.purchase_tax_rate = purchasesTaxRate?.isZero()
          ? this.previousValue.purchase_tax_rate
          : category.purchase_tax_rate;

        this.crudGrid.data[index] = category;
      });
  }

  updateTax(category: ProductCategoryModel): void {
    if (this.isQuickbooksTaxTypeAvailable()) {
      category.purchase_tax_rate = category.qbo_purchase_tax_type
        ? this.taxRates.find(rate => rate.id === category.qbo_purchase_tax_type)?.purchase_tax_value
        : null;

      category.sales_tax_rate = category.qbo_sales_tax_type
        ? this.taxRates.find(rate => rate.id === category.qbo_sales_tax_type)?.sales_tax_value
        : null;
    } else if (this.isXeroTaxTypeAvailable()) {
      category.purchase_tax_rate = category.xero_purchase_tax_type
        ? this.taxRates.find(rate => rate.tax_type === category.xero_purchase_tax_type)?.effective_rate
        : null;

      category.sales_tax_rate = category.xero_sales_tax_type
        ? this.taxRates.find(rate => rate.tax_type === category.xero_sales_tax_type)?.effective_rate
        : null;
    }
  }

  toggle(): void {
    this.collapsed = !this.collapsed;
  }
}
